import { FormType, ReportJSON } from '@aloft/toolkit-shared';

import { generateTotalXmlAdjustmentNodes } from './generateTotalXmlAdjustmentNodes.ts';
import { generateTotalXmlAnnotationNodes } from './generateTotalXmlAnnotationNodes.ts';
import { createDirtyNode } from './utils.ts';

export function createSalesCompNode(
  doc: XMLDocument,
  { compSequence, comps, subject }: ReportJSON,
  formType: FormType,
  includeAnnotations: boolean,
): HTMLElement {
  const salesComp = createDirtyNode(doc, 'SALESCOMP');

  const subjectNode = createSubjectNode(doc, subject.property, formType, includeAnnotations);
  salesComp.appendChild(subjectNode);

  for (const [i, compID] of compSequence.entries()) {
    const comp = comps[compID];
    const compNode = comp && createCompsNode(doc, i, comp, formType, includeAnnotations);

    if (compNode) {
      salesComp.appendChild(compNode);
    }
  }

  return salesComp;
}

function createSubjectNode(
  doc: XMLDocument,
  subject: ReportJSON['subject']['property'],
  formType: FormType,
  includeAnnotations: boolean,
): HTMLElement {
  const subjectNode = createDirtyNode(doc, 'SUBJECT');
  subjectNode.setAttribute('PROPERTYTYPE', getSalesCompPropertyType(formType));
  subjectNode.setAttribute('LATITUTDE', subject.salesGridData.annotations.LatitudeNumber ?? '');
  subjectNode.setAttribute('LONGITUDE', subject.salesGridData.annotations.LongitudeNumber ?? '');

  if (includeAnnotations) {
    const annotationNodes = generateTotalXmlAnnotationNodes(doc, subject);
    for (const annotation of annotationNodes) {
      subjectNode.appendChild(annotation);
    }
  }

  return subjectNode;
}

function createCompsNode(
  doc: XMLDocument,
  compIndex: number,
  comp: ReportJSON['comps'][string],
  formType: FormType,
  includeAnnotations: boolean,
): HTMLElement {
  const comps = createDirtyNode(doc, 'COMPS');
  comps.setAttribute('NUM', (compIndex + 1).toString());
  comps.setAttribute('PROPERTYTYPE', getSalesCompPropertyType(formType));
  comps.setAttribute('LATITUTDE', comp.salesGridData.annotations.LatitudeNumber ?? '');
  comps.setAttribute('LONGITUDE', comp.salesGridData.annotations.LongitudeNumber ?? '');

  if (includeAnnotations) {
    const annotationNodes = generateTotalXmlAnnotationNodes(doc, comp);
    for (const annotation of annotationNodes) {
      comps.appendChild(annotation);
    }
  }

  // All exports include the Adjustments
  const adjNodes = generateTotalXmlAdjustmentNodes(doc, comp, formType);
  for (const adj of adjNodes) {
    comps.appendChild(adj);
  }

  return comps;
}

function getSalesCompPropertyType(formType: FormType): string {
  switch (formType) {
    case FormType.fnma1004:
    case FormType.fnma1004c:
    case FormType.fnma2055:
    case FormType.gpResidential:
      return 'Residential';
    case FormType.fnma1025:
      return 'Commercial';
  }
}
